import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import useUserQuery from '../../hooks/queries/useUserQuery';
import { AdMob } from '../../../utils/admob';
import type { AdMobRewardData, AdMobErrorData } from '../../../types/admob';

// 광고 이벤트 처리 소유자(광고를 실제로 띄운 훅 인스턴스) 추적
let activeAdOwnerId: symbol | null = null;

interface UseAdMobOptions {
  adUnitId?: string; // 특정 광고 단위 ID (선택)
  adType?: 'rewarded' | 'rewardedInterstitial' | 'interstitial'; // 광고 타입 (선택)
  autoLoad?: boolean; // 자동 로드 여부 (기본: true)
  featureKind?: 'attendance' | 'ads' | 'interstitial'; // 기본 단위 선택용 (선택)
  onReward?: (data: AdMobRewardData) => void;
  onAdClosed?: () => void;
  onError?: (error: string) => void;
}

export const useAdMob = ({
  adUnitId,
  adType = 'rewardedInterstitial',
  autoLoad = true,
  featureKind,
  onReward,
  onAdClosed,
  onError
}: UseAdMobOptions) => {
  const [isAdReady, setIsAdReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: user } = useUserQuery();
  const userId = user?.userId ?? '';

  // 내부 가드/콜백 안정화용 ref
  const isLoadingRef = useRef(false);
  const isAdReadyRef = useRef(false);
  const lastLoadAtRef = useRef<number>(0);
  const ownerIdRef = useRef(Symbol('admob-owner'));
  const rewardHandledRef = useRef(false);
  const hasClosedRef = useRef(false);
  const shouldShowAfterLoadRef = useRef(false);
  const onRewardRef = useRef(onReward);
  const onAdClosedRef = useRef(onAdClosed);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onRewardRef.current = onReward;
  }, [onReward]);

  useEffect(() => {
    onAdClosedRef.current = onAdClosed;
  }, [onAdClosed]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // 광고 로드
  const loadAd = useCallback(() => {
    if (!userId) {
      return;
    }

    // 중복 로드 가드 및 간단 쿨다운(1초)
    const now = Date.now();
    if (isLoadingRef.current || isAdReadyRef.current || now - lastLoadAtRef.current < 1000) {
      return;
    }
    isLoadingRef.current = true;
    lastLoadAtRef.current = now;
    setIsLoading(true);
    setError(null);
    AdMob.loadRewardedAd(userId, adUnitId, adType, featureKind);
  }, [userId, adUnitId, adType, featureKind]);

  // 광고 표시
  const showAd = useCallback(() => {
    // 현재 훅 인스턴스를 활성 소유자로 지정하고 플래그 초기화
    activeAdOwnerId = ownerIdRef.current;
    hasClosedRef.current = false;
    rewardHandledRef.current = false;
    if (!isAdReady) {
      // 준비되지 않았다면 로드 후 즉시 표시 예약
      shouldShowAfterLoadRef.current = true;
      // 로드 시도(가드 내장)
      loadAd();
      return;
    }
    shouldShowAfterLoadRef.current = false;
    AdMob.showRewardedAd(adUnitId, featureKind, adType);
  }, [isAdReady, adUnitId, featureKind, adType, loadAd]);

  useEffect(() => {
    // 이벤트 핸들러
    const handleAdLoaded = () => {
      console.log('✅ AdMob: Ad loaded');
      setIsAdReady(true);
      isAdReadyRef.current = true;
      setIsLoading(false);
      isLoadingRef.current = false;
      setError(null);
      rewardHandledRef.current = false;
      hasClosedRef.current = false;
      if (activeAdOwnerId === ownerIdRef.current && shouldShowAfterLoadRef.current) {
        // 표시 예약이 있고 이 훅이 소유자라면 즉시 표시
        shouldShowAfterLoadRef.current = false;
        AdMob.showRewardedAd(adUnitId, featureKind, adType);
      }
    };

    const handleAdFailedToLoad = (e: CustomEvent<AdMobErrorData>) => {
      console.error('❌ AdMob: Failed to load:', e.detail.error);
      setIsAdReady(false);
      isAdReadyRef.current = false;
      setIsLoading(false);
      isLoadingRef.current = false;
      setError(e.detail.error);
      onErrorRef.current?.(e.detail.error);
    };

    const handleAdNotReady = () => {
      setIsAdReady(false);
      isAdReadyRef.current = false;
    };

    const handleUserEarnedReward = (e: CustomEvent<AdMobRewardData>) => {
      if (activeAdOwnerId !== ownerIdRef.current || rewardHandledRef.current) {
        // 소유자가 아니거나 이미 처리함
        return;
      }
      rewardHandledRef.current = true;
      onRewardRef.current?.(e.detail);
      queryClient.invalidateQueries(['bettieBalances']);
    };

    const handleAdClosed = () => {
      if (activeAdOwnerId !== ownerIdRef.current || hasClosedRef.current) {
        // 소유자가 아니거나 이미 처리함
        return;
      }
      hasClosedRef.current = true;
      setIsAdReady(false);
      isAdReadyRef.current = false;
      isLoadingRef.current = false;

      if (autoLoad) {
        // 광고 닫힌 후 다음 광고 미리 로드
        setTimeout(() => loadAd(), 1000);
      }

      onAdClosedRef.current?.();
      // 소유권 해제
      activeAdOwnerId = null;
    };

    const handleAdFailedToShow = (e: CustomEvent<AdMobErrorData>) => {
      console.error('❌ AdMob: Failed to show:', e.detail.error);
      setIsAdReady(false);
      setError(e.detail.error);
      isAdReadyRef.current = false;
      onErrorRef.current?.(e.detail.error);
      if (activeAdOwnerId === ownerIdRef.current) {
        activeAdOwnerId = null;
      }
    };

    // 이벤트 리스너 등록
    const loadedEvents = [
      'admob:adLoaded',
      'admob:rewardedInterstitialAdLoaded',
      'admob:rewardedAdLoaded',
      'admob:interstitialAdLoaded'
    ];
    const failedToLoadEvents = [
      'admob:adFailedToLoad',
      'admob:rewardedInterstitialAdFailedToLoad',
      'admob:rewardedAdFailedToLoad',
      'admob:interstitialAdFailedToLoad'
    ];
    const notReadyEvents = [
      'admob:adNotReady',
      'admob:rewardedInterstitialAdNotReady',
      'admob:rewardedAdNotReady',
      'admob:interstitialAdNotReady'
    ];
    const rewardEvents = [
      'admob:userEarnedReward',
      'admob:rewardedInterstitialAdUserEarnedReward',
      'admob:rewardedAdUserEarnedReward'
    ];
    const closedEvents = [
      'admob:adClosed',
      'admob:rewardedInterstitialAdClosed',
      'admob:rewardedAdClosed',
      'admob:interstitialAdClosed'
    ];
    const failedToShowEvents = [
      'admob:adFailedToShow',
      'admob:rewardedInterstitialAdFailedToShow',
      'admob:rewardedAdFailedToShow',
      'admob:interstitialAdFailedToShow'
    ];

    loadedEvents.forEach((evt) => window.addEventListener(evt, handleAdLoaded as EventListener));
    failedToLoadEvents.forEach((evt) =>
      window.addEventListener(evt, handleAdFailedToLoad as EventListener)
    );
    notReadyEvents.forEach((evt) =>
      window.addEventListener(evt, handleAdNotReady as EventListener)
    );
    // 보상 이벤트는 인터스티셜에는 해당 없음. 그래도 통합 수신(무해)
    rewardEvents.forEach((evt) =>
      window.addEventListener(evt, handleUserEarnedReward as EventListener)
    );
    closedEvents.forEach((evt) => window.addEventListener(evt, handleAdClosed as EventListener));
    failedToShowEvents.forEach((evt) =>
      window.addEventListener(evt, handleAdFailedToShow as EventListener)
    );

    // 자동 로드
    if (autoLoad) {
      loadAd();
    }

    return () => {
      loadedEvents.forEach((evt) => window.removeEventListener(evt, handleAdLoaded as EventListener));
      failedToLoadEvents.forEach((evt) =>
        window.removeEventListener(evt, handleAdFailedToLoad as EventListener)
      );
      notReadyEvents.forEach((evt) =>
        window.removeEventListener(evt, handleAdNotReady as EventListener)
      );
      rewardEvents.forEach((evt) =>
        window.removeEventListener(evt, handleUserEarnedReward as EventListener)
      );
      closedEvents.forEach((evt) =>
        window.removeEventListener(evt, handleAdClosed as EventListener)
      );
      failedToShowEvents.forEach((evt) =>
        window.removeEventListener(evt, handleAdFailedToShow as EventListener)
      );
    };
  }, [autoLoad, loadAd, queryClient, adUnitId, featureKind, adType]);

  return {
    isAdReady,
    isLoading,
    error,
    loadAd,
    showAd
  };
};
